import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import Button, { BUTTON_COLOR_KEYS } from 'components/common/Button';
import CheckIcon from 'icons/CheckIcon';

/* HOCs */
import BaseModalHOC from 'components/hoc/BaseModalHOC';
import type { InjectedPropsType as BaseModalHOCInjectedPropsType } from 'components/hoc/BaseModalHOC';

/* Styled Components */
import {
  Wrapper,
  Header,
  BodyText,
  InputWrapper,
} from 'styles/common/HelpModal';

/* Child Components */
import WishTextInput from 'components/common/WishTextInput';

/* Services */
import GenerateLeadService from 'services/GenerateLeadService';

/* Utils */
import { i18n, ni18n } from 'utils/i18n';
import { normalizePhoneNumber } from 'utils/PhoneNumber';
import { isValidEmailAddress } from 'utils/FormValidators';
import {
  sendGAEvent,
  GA_GROUP_NAME,
  EVENT_CATEGORY,
  EVENT_ACTION,
  LEAD_LABEL,
} from 'utils/GoogleAnalytics';

const HEAR_BACK_DAYS = 3;

type PropsType = BaseModalHOCInjectedPropsType;

const BlueHelpModal = ({ closeModal }: PropsType) => {
  const theme = useTheme();
  const [name, setName] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
  const [address, setAddress] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const enableSubmit = Boolean(
    name && phoneNumber && address && isValidEmailAddress(email),
  );

  const onPhoneNumberChange = (
    event: React.SyntheticEvent<HTMLInputElement>,
  ) => {
    const normalizedPhoneNumber = normalizePhoneNumber(
      event.currentTarget.value,
    );
    setPhoneNumber(normalizedPhoneNumber);
  };

  const onEmailChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    setIsValidEmail(true);
    setEmail(event.currentTarget.value);
  };

  const onClickSubmit = () => {
    if (enableSubmit) {
      setShowSuccess(true);
      GenerateLeadService({
        name,
        phoneNumber,
        email,
        address,
      });
      sendGAEvent(GA_GROUP_NAME, EVENT_ACTION.GENERATE_LEAD, {
        event_category: EVENT_CATEGORY.LANDING_PAGE,
        event_label: LEAD_LABEL.PHONE_NUMBER,
      });
    }
  };

  if (showSuccess) {
    return (
      <Wrapper>
        <CheckIcon
          overrideStyle={{
            width: 36,
            height: 36,
            padding: 8,
            marginBottom: 20,
            backgroundColor: theme.PickupGreen,
            borderRadius: '50%',
            color: theme.white,
          }}
        />
        <Header>{i18n('Talk to you soon!')}</Header>
        <BodyText>
          {ni18n(
            HEAR_BACK_DAYS,
            'A Wish Local team member will be in touch within 1 business day',
            'A Wish Local team member will be in touch within {%1=number of business days} business days',
          )}
        </BodyText>
        <Button
          stretch
          color={BUTTON_COLOR_KEYS.DARK_COBALT}
          onClick={closeModal}
          overrideStyle={{ marginTop: 24 }}
        >
          {i18n('Got it')}
        </Button>
      </Wrapper>
    );
  }

  return (
    <Wrapper alignLeft>
      <Header margin="0 32px 0 0">
        {i18n('Request a call with our team')}
      </Header>
      <BodyText>
        {`${i18n('Fill out this form to have us call you.')} 
          ${ni18n(
            HEAR_BACK_DAYS,
            "We'll do our best to reach out within 1 business day.",
            "We'll do our best to reach out within {%1=number of business days} business days.",
          )}`}
      </BodyText>
      <InputWrapper>
        <WishTextInput
          stretch
          title={i18n('Name')}
          titleBold
          value={name}
          onChange={event => setName(event.currentTarget.value)}
        />
      </InputWrapper>
      <InputWrapper>
        <WishTextInput
          stretch
          title={i18n('Phone number')}
          titleBold
          value={phoneNumber}
          onChange={onPhoneNumberChange}
        />
      </InputWrapper>
      <InputWrapper>
        <WishTextInput
          stretch
          title={i18n('Email')}
          titleBold
          value={email}
          onChange={onEmailChange}
          onBlur={() => {
            setIsValidEmail(isValidEmailAddress(email));
          }}
          isErrorShown={!isValidEmail}
          errorMessage={i18n('Invalid email format')}
        />
      </InputWrapper>
      <InputWrapper>
        <WishTextInput
          stretch
          title={i18n('Address')}
          titleBold
          value={address}
          onChange={event => setAddress(event.currentTarget.value)}
        />
      </InputWrapper>
      <Button
        stretch
        disabled={!enableSubmit}
        color={BUTTON_COLOR_KEYS.DARK_COBALT}
        onClick={onClickSubmit}
        overrideStyle={{ marginTop: 16 }}
      >
        {i18n('Request a call')}
      </Button>
    </Wrapper>
  );
};

export default BaseModalHOC(BlueHelpModal, {
  style: {
    margin: 'auto 16px',
    padding: '24px',
  },
});
